import { useEffect, useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { mergeRegister } from '@lexical/utils';
import { Box, Button, Popper, SxProps, Theme, Typography } from '@mui/material';
import { $getSelection, $isRangeSelection, BLUR_COMMAND, COMMAND_PRIORITY_LOW } from 'lexical';

import { useRichTextEditor } from './editor';

const STRINGS = appStrings.common.richTextEditor;

const isOpenableUrl = (href: string) => /^(https?:|mailto:)/i.test(href);

const openInNewTab = (href: string) => window.open(href, '_blank', 'noopener,noreferrer');

/**
 * While the caret sits inside a link, shows its address with an Open button.
 * Ctrl/Cmd + click on a link opens it directly.
 *
 * Must render inside `RichTextProvider`.
 */
export const LinkPopover = () => {
  const { editor } = useRichTextEditor();
  const [anchor, setAnchor] = useState<HTMLAnchorElement | null>(null);

  useEffect(() => {
    if (!editor) return;

    const syncToSelection = () => {
      editor.getEditorState().read(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          setAnchor(null);

          return;
        }

        const link = editor.getElementByKey(selection.anchor.getNode().getKey())?.closest('a');

        setAnchor(link && isOpenableUrl(link.href) ? link : null);
      });
    };

    return mergeRegister(
      editor.registerUpdateListener(syncToSelection),
      editor.registerCommand(
        BLUR_COMMAND,
        () => {
          setAnchor(null);

          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor]);

  useEffect(() => {
    if (!editor) return;

    const handleClick = (event: MouseEvent) => {
      if (!event.metaKey && !event.ctrlKey) return;

      const link = (event.target as HTMLElement | null)?.closest('a');

      if (!link || !isOpenableUrl(link.href)) return;

      event.preventDefault();
      openInNewTab(link.href);
    };

    return editor.registerRootListener((root, previousRoot) => {
      previousRoot?.removeEventListener('click', handleClick);
      root?.addEventListener('click', handleClick);
    });
  }, [editor]);

  return (
    <Popper
      open={!!anchor}
      anchorEl={anchor}
      placement="bottom-start"
      sx={popperSx}
      modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
    >
      {/* Keeps editor focus on mousedown so the blur handler doesn't close this before the click lands. */}
      <Box sx={containerSx} onMouseDown={(event) => event.preventDefault()}>
        <Typography variant="text-sm-regular" sx={urlSx} title={anchor?.href}>
          {anchor?.href}
        </Typography>
        <Button
          id="rt-link-popover-open-btn"
          hierarchy="Secondary gray"
          size="sm"
          aria-label={STRINGS.openLinkAria}
          endIcon={<AppIcon iconName="LinkExternal01" diameter="14px" color="currentColor" />}
          onClick={() => anchor && openInNewTab(anchor.href)}
        >
          {STRINGS.openLink}
        </Button>
      </Box>
    </Popper>
  );
};

const popperSx: SxProps<Theme> = ({ zIndex }) => ({
  zIndex: zIndex.modal + 1,
});

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  padding: backrs.spacing['spacing-sm'],
  paddingLeft: backrs.spacing['spacing-lg'],
  maxWidth: 360,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-md'],
  boxShadow: backrs.shadows['shadow-lg'],
});

const urlSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
});
